(() => {
  const roots = new WeakMap();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopFine = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

  const getGsap = () => window.gsap;
  const gsapLoads = new Map();
  const gsapEligible = (root) => Boolean(root && !reduced.matches && desktopFine.matches && !navigator.connection?.saveData);
  const ensureGsap = (root) => {
    if (root) root.dataset.mhhGsapStatus = 'checking';
    if (getGsap()) { if (root) root.dataset.mhhGsapStatus = 'already-loaded'; return Promise.resolve(getGsap()); }
    if (!gsapEligible(root) || !root?.dataset?.mhhGsapSrc) { if (root) root.dataset.mhhGsapStatus = 'ineligible-or-missing-src'; return Promise.resolve(null); }
    const src = root.dataset.mhhGsapSrc;
    if (gsapLoads.has(src)) return gsapLoads.get(src);
    if (window.__muaazonGsapPromise) {
      const shared = window.__muaazonGsapPromise.then((gsap) => {
        if (root) root.dataset.mhhGsapStatus = gsap ? 'loaded' : 'load-error';
        return gsap;
      });
      gsapLoads.set(src, shared);
      return shared;
    }
    window.__muaazonGsapPromise = new Promise((resolve) => {
      const existing = document.querySelector('script[data-muaazon-gsap-loader]');
      if (existing) {
        existing.addEventListener('load', () => resolve(getGsap() || null), { once: true });
        existing.addEventListener('error', () => resolve(null), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.muaazonGsapLoader = 'true';
      script.addEventListener('load', () => resolve(getGsap() || null), { once: true });
      script.addEventListener('error', () => resolve(null), { once: true });
      document.head.appendChild(script);
    });
    const promise = window.__muaazonGsapPromise.then((gsap) => {
      if (root) root.dataset.mhhGsapStatus = gsap ? 'loaded' : 'load-error';
      return gsap;
    });
    gsapLoads.set(src, promise);
    return promise;
  };
  const activeSlide = (root) => root.querySelector('[data-mhh-slide].is-active') || root.querySelector('[data-mhh-slide]');

  const animateSlide = (root, slide, initial = false) => {
    const gsap = getGsap();
    if (!slide || reduced.matches || !desktopFine.matches || !gsap) return;

    const image = slide.querySelector('.mz-home-hero__media img');
    const copy = slide.querySelector('.mz-home-hero__copy');
    const items = copy ? [...copy.children] : [];

    if (image) {
      gsap.killTweensOf(image);
      gsap.fromTo(
        image,
        { scale: initial ? 1.018 : 1.032, xPercent: initial ? 0 : 0.42 },
        { scale: 1, xPercent: 0, duration: initial ? 1.05 : 1.16, ease: 'power3.out', overwrite: true }
      );
    }

    if (items.length) {
      gsap.killTweensOf(items);
      if (initial) {
        gsap.set(items, { autoAlpha: 1, y: 0, clearProps: 'transform,opacity,visibility' });
      } else {
        gsap.fromTo(
          items,
          { autoAlpha: 0, y: 18 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.56,
            stagger: 0.06,
            ease: 'power3.out',
            overwrite: true,
            immediateRender: false,
            clearProps: 'transform,opacity,visibility'
          }
        );
      }
    }
  };

  const threeEligible = (root) => {
    if (root) root.dataset.mhhThreeStatus = 'checking';
    if (!root || reduced.matches || !desktopFine.matches) { if (root) root.dataset.mhhThreeStatus = 'ineligible-viewport-or-motion'; return false; }
    if (navigator.connection?.saveData) { root.dataset.mhhThreeStatus = 'save-data'; return false; }
    if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4) { root.dataset.mhhThreeStatus = 'low-memory'; return false; }
    if (!root.dataset.mhhThreeSrc || !root.querySelector('[data-mhh-depth]')) { root.dataset.mhhThreeStatus = 'missing-src-or-canvas'; return false; }
    try {
      const probe = document.createElement('canvas');
      const ok = Boolean(probe.getContext('webgl2') || probe.getContext('webgl')); root.dataset.mhhThreeStatus = ok ? 'eligible' : 'no-webgl'; return ok;
    } catch {
      root.dataset.mhhThreeStatus = 'webgl-probe-error'; return false;
    }
  };

  const createDepth = async (root) => {
    if (!threeEligible(root) || root.dataset.mhhThreeInit === 'true') return null;
    root.dataset.mhhThreeInit = 'true';

    let THREE;
    try {
      root.dataset.mhhThreeStatus = 'importing';
      THREE = await import(root.dataset.mhhThreeSrc);
      root.dataset.mhhThreeStatus = 'imported';
    } catch (error) {
      root.dataset.mhhThreeInit = 'failed';
      root.dataset.mhhThreeStatus = 'import-error';
      root.dataset.mhhThreeError = String(error?.message || error).slice(0, 120);
      return null;
    }

    const canvas = root.querySelector('[data-mhh-depth]');
    if (!canvas || !root.isConnected) return null;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: false,
        powerPreference: 'high-performance'
      });
    } catch {
      root.dataset.mhhThreeInit = 'failed';
      root.dataset.mhhThreeStatus = 'renderer-error';
      return null;
    }

    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 20);
    camera.position.set(0, 0, 5.8);

    const group = new THREE.Group();
    scene.add(group);

    const materials = [];
    [
      [1.18, 0.018, 0.27, 0.15],
      [0.88, 0.014, -0.31, 0.38],
      [0.61, 0.012, 0.46, -0.22]
    ].forEach(([radius, tube, rx, ry], index) => {
      const material = new THREE.MeshBasicMaterial({
        color: 0xf5b82e,
        transparent: true,
        opacity: index === 0 ? 0.34 : 0.22,
        depthWrite: false,
        blending: THREE.AdditiveBlending
      });
      materials.push(material);
      const mesh = new THREE.Mesh(
        new THREE.TorusGeometry(radius, tube, 6, index === 0 ? 96 : 72),
        material
      );
      mesh.rotation.set(rx, ry, index * 0.55);
      group.add(mesh);
    });

    const coreMaterial = new THREE.MeshBasicMaterial({
      color: 0xf5b82e,
      wireframe: true,
      transparent: true,
      opacity: 0.13,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    materials.push(coreMaterial);
    const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.34, 1), coreMaterial);
    core.position.set(0.15, -0.05, 0.18);
    group.add(core);

    const pointPositions = [];
    for (let i = 0; i < 24; i += 1) {
      const angle = (i / 24) * Math.PI * 2;
      const radius = 0.72 + ((i * 17) % 13) / 15;
      pointPositions.push(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius * 0.62,
        ((i % 7) - 3) * 0.08
      );
    }
    const pointGeometry = new THREE.BufferGeometry();
    pointGeometry.setAttribute('position', new THREE.Float32BufferAttribute(pointPositions, 3));
    const pointMaterial = new THREE.PointsMaterial({
      color: 0xf5b82e,
      size: 0.028,
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    materials.push(pointMaterial);
    group.add(new THREE.Points(pointGeometry, pointMaterial));

    let targetX = 0;
    let targetY = 0;
    let frame = 0;
    let visible = true;
    let destroyed = false;
    const clock = new THREE.Clock();

    const setSide = (slide) => {
      const side = slide?.dataset.contentHorizontal === 'right' ? -1 : 1;
      group.position.x = 1.5 * side;
      group.position.y = 0.02;
    };

    const moveCanvas = (slide) => {
      if (!slide || !canvas.isConnected) return;
      const shade = slide.querySelector('.mz-home-hero__shade');
      if (shade && canvas.parentElement !== slide) slide.insertBefore(canvas, shade);
      setSide(slide);
    };

    const setAccent = (slide, animate = true) => {
      if (!slide) return;
      const cssColor = getComputedStyle(slide).getPropertyValue('--mhh-slide-accent').trim() || '#f5b82e';
      const color = new THREE.Color(cssColor);
      const gsap = getGsap();
      materials.forEach((material) => {
        if (animate && gsap) {
          gsap.to(material.color, {
            r: color.r, g: color.g, b: color.b,
            duration: 0.7,
            ease: 'power2.out',
            overwrite: true
          });
        } else {
          material.color.copy(color);
        }
      });
    };

    const resize = () => {
      if (destroyed) return;
      const rect = root.querySelector('.mz-home-hero__stage')?.getBoundingClientRect();
      if (!rect?.width || !rect?.height) return;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.35));
      renderer.setSize(rect.width, rect.height, false);
      camera.aspect = rect.width / rect.height;
      camera.updateProjectionMatrix();
    };

    const render = () => {
      frame = 0;
      if (destroyed || !visible || document.hidden) return;
      const t = clock.getElapsedTime();
      group.rotation.z += (targetX * 0.18 + Math.sin(t * 0.22) * 0.025 - group.rotation.z) * 0.035;
      group.rotation.x += (-targetY * 0.08 - group.rotation.x) * 0.03;
      group.position.y = Math.sin(t * 0.42) * 0.025;
      core.rotation.x = t * 0.075;
      core.rotation.y = t * 0.11;
      camera.position.x += (targetX * 0.08 - camera.position.x) * 0.035;
      camera.position.y += (-targetY * 0.05 - camera.position.y) * 0.035;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
      frame = requestAnimationFrame(render);
    };

    const requestRender = () => {
      if (!frame && !destroyed && visible && !document.hidden) frame = requestAnimationFrame(render);
    };

    const onPointer = (event) => {
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      targetX = Math.max(-1, Math.min(1, ((event.clientX - rect.left) / rect.width - 0.5) * 2));
      targetY = Math.max(-1, Math.min(1, ((event.clientY - rect.top) / rect.height - 0.5) * 2));
    };
    const onLeave = () => { targetX = 0; targetY = 0; };

    const onHeroChange = (event) => {
      const slide = event.detail?.slide;
      if (!slide) return;
      moveCanvas(slide);
      setAccent(slide, true);
      const gsap = getGsap();
      if (gsap) {
        gsap.to(group.rotation, { y: group.rotation.y + 0.26, duration: 1.0, ease: 'power3.out', overwrite: 'auto' });
        const depthOpacity = Math.max(0, Math.min(1, parseFloat(getComputedStyle(root).getPropertyValue('--mhh-cinematic-opacity')) || 0.58));
        gsap.fromTo(canvas, { opacity: 0.12 }, { opacity: depthOpacity, duration: 0.72, ease: 'power2.out', overwrite: true, clearProps: 'opacity' });
      }
      requestRender();
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(root);

    const visibilityObserver = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting);
      if (visible) requestRender();
      else if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
    }, { threshold: 0.02 });
    visibilityObserver.observe(root);

    const onDocumentVisibility = () => {
      if (document.hidden) {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
      } else if (visible) {
        requestRender();
      }
    };
    document.addEventListener('visibilitychange', onDocumentVisibility);

    root.addEventListener('pointermove', onPointer, { passive: true });
    root.addEventListener('pointerleave', onLeave, { passive: true });
    root.addEventListener('muaazon:hero:change', onHeroChange);

    const first = activeSlide(root);
    moveCanvas(first);
    setAccent(first, false);
    resize();
    root.dataset.mhhThreeReady = 'true';
    root.dataset.mhhThreeStatus = 'ready';
    requestRender();

    return {
      destroy() {
        destroyed = true;
        if (frame) cancelAnimationFrame(frame);
        resizeObserver.disconnect();
        visibilityObserver.disconnect();
        document.removeEventListener('visibilitychange', onDocumentVisibility);
        root.removeEventListener('pointermove', onPointer);
        root.removeEventListener('pointerleave', onLeave);
        root.removeEventListener('muaazon:hero:change', onHeroChange);
        renderer.dispose();
        pointGeometry.dispose();
        group.traverse((node) => node.geometry?.dispose?.());
        materials.forEach((material) => material.dispose?.());
        delete root.dataset.mhhThreeReady;
      }
    };
  };

  const scheduleGsap = (root) => {
    if (!gsapEligible(root)) return;
    const run = () => ensureGsap(root).then((gsap) => {
      if (gsap && root.isConnected) animateSlide(root, activeSlide(root), true);
    });
    const afterLoad = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 900 });
      else setTimeout(run, 250);
    };
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  };

  const scheduleDepth = (root) => {
    if (!threeEligible(root)) return;
    const run = () => createDepth(root).then((depth) => {
      const state = roots.get(root);
      if (state && depth) state.depth = depth;
    });
    const afterLoad = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 1800 });
      else setTimeout(run, 550);
    };
    if (document.readyState === 'complete') afterLoad();
    else window.addEventListener('load', afterLoad, { once: true });
  };

  const init = (root) => {
    if (!root || root.dataset.mhhCinematicReady === 'true') return;
    root.dataset.mhhCinematicReady = 'true';
    root.classList.add('is-cinematic');

    const onChange = (event) => animateSlide(root, event.detail?.slide, false);
    root.addEventListener('muaazon:hero:change', onChange);

    roots.set(root, { onChange, depth: null });
    scheduleGsap(root);
    scheduleDepth(root);
  };

  const mount = (scope = document) => {
    const nodes = [];
    if (scope instanceof Element && scope.matches('[data-mhh-root][data-mhh-cinematic="true"]')) nodes.push(scope);
    scope.querySelectorAll?.('[data-mhh-root][data-mhh-cinematic="true"]').forEach((node) => nodes.push(node));
    nodes.forEach(init);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target?.matches?.('[data-mhh-root]') ? event.target : event.target?.querySelector?.('[data-mhh-root]');
    const state = root ? roots.get(root) : null;
    state?.depth?.destroy?.();
    if (root && state?.onChange) root.removeEventListener('muaazon:hero:change', state.onChange);
    if (root) roots.delete(root);
  });
})();